import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { X, Plus, Trash2, ShieldCheck, Users } from "lucide-react";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ key: "", name: "", description: "", permissions: [] });

  const load = async () => {
    try {
      const [r, p, u] = await Promise.all([
        api.get("/admin/rbac/roles"),
        api.get("/admin/rbac/permissions"),
        api.get("/admin/users"),
      ]);
      setRoles(r.data);
      setPermissions(p.data);
      setUsers(u.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (role) => {
    setActive(role);
    setForm({ key: role.key, name: role.name, description: role.description || "", permissions: role.permissions || [] });
  };
  const openCreate = () => {
    setCreating(true);
    setActive(null);
    setForm({ key: "", name: "", description: "", permissions: [] });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (creating) {
        await api.post("/admin/rbac/roles", form);
        toast.success("Role created");
      } else if (active) {
        await api.patch(`/admin/rbac/roles/${active.id}`, { name: form.name, description: form.description, permissions: form.permissions });
        toast.success("Role updated");
      }
      setActive(null);
      setCreating(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const del = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.delete(`/admin/rbac/roles/${role.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const assignRole = async (user_id, role_key) => {
    try {
      await api.patch(`/admin/rbac/users/${user_id}/role`, { role: role_key });
      toast.success("Role assigned");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const togglePerm = (p) => {
    setForm({ ...form, permissions: form.permissions.includes(p) ? form.permissions.filter(x => x !== p) : [...form.permissions, p] });
  };

  const groupedPerms = permissions.reduce((acc, p) => {
    const g = p.split(".")[0];
    (acc[g] = acc[g] || []).push(p);
    return acc;
  }, {});

  return (
    <div>
      <SectionTitle eyebrow="Access control" title="Roles & Permissions" right={
        <button onClick={openCreate} className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="role-new-btn"><Plus size={14}/> New role</button>
      } />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="ldc-glass p-2 max-h-[70vh] overflow-y-auto ldc-scroll" data-testid="roles-list">
          {roles.map((r) => (
            <div key={r.id} onClick={() => openEdit(r)} className={`p-4 rounded-md border cursor-pointer transition-colors mb-1 ${active?.id === r.id ? "border-[#fbce18]/50 bg-white/[0.04]" : "border-transparent hover:bg-white/5"}`} data-testid={`role-item-${r.key}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-[#fbce18]"/>
                    <div className="font-display font-semibold">{r.name}</div>
                    {r.system && <span className="ldc-badge ldc-badge-pending">System</span>}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5 font-mono">/{r.key}</div>
                  {r.description && <div className="text-xs text-white/50 mt-1">{r.description}</div>}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.permissions.slice(0, 6).map(p => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/60 font-mono">{p}</span>
                    ))}
                    {r.permissions.length > 6 && <span className="text-[10px] text-white/40">+{r.permissions.length - 6}</span>}
                  </div>
                </div>
                {!r.system && (
                  <button onClick={(e) => { e.stopPropagation(); del(r); }} className="p-1.5 rounded hover:bg-white/5 text-white/60 hover:text-red-400 transition-colors" data-testid={`role-del-${r.key}`}>
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="ldc-glass p-5" data-testid="role-detail">
          {!active && !creating ? (
            <div className="text-center py-20 text-white/40 text-sm">Select a role to edit, or click "New role" to create one.</div>
          ) : (
            <form onSubmit={save}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl">{creating ? "New role" : `Edit · ${active.name}`}</h3>
                {creating && <button type="button" onClick={() => setCreating(false)} className="p-1.5 rounded hover:bg-white/5"><X size={16}/></button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {creating ? (
                  <F label="Key (slug)" v={form.key} on={(v) => setForm({ ...form, key: v.toLowerCase().replace(/\s+/g, "_") })} testid="role-input-key" required />
                ) : (
                  <label className="block">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Key</div>
                    <div className="ldc-input rounded-md px-3 py-2 text-sm text-white/50">{form.key}</div>
                  </label>
                )}
                <F label="Display name" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="role-input-name" required />
              </div>
              <label className="block mt-3">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Description</div>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="role-input-desc" />
              </label>

              <div className="mt-5">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-2">Permissions</div>
                <div className="space-y-3 max-h-[45vh] overflow-y-auto ldc-scroll pr-2">
                  {Object.entries(groupedPerms).map(([group, perms]) => (
                    <div key={group}>
                      <div className="text-xs text-[#fbce18]/80 uppercase tracking-[0.14em] mb-1">{group}</div>
                      <div className="grid grid-cols-2 gap-1">
                        {perms.map(p => (
                          <label key={p} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 text-xs">
                            <input
                              type="checkbox"
                              checked={form.permissions.includes(p)}
                              onChange={() => togglePerm(p)}
                              className="accent-[#fbce18]"
                              disabled={!creating && active?.key === "super_admin"}
                              data-testid={`role-perm-${p}`}
                            />
                            <span className="font-mono text-white/75">{p}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-5">
                {!creating && <button type="button" onClick={() => setActive(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm">Close</button>}
                <button type="submit" className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="role-save" disabled={!creating && active?.key === "super_admin"}>Save</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* User → role assignment */}
      <div className="mt-8">
        <SectionTitle eyebrow="Users" title="Assign roles" />
        <div className="ldc-glass overflow-hidden">
          <table className="w-full ldc-table" data-testid="user-role-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Current role</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`urow-${u.user_id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#fbce18]/20 flex items-center justify-center text-[#fbce18] text-xs font-semibold">{u.name?.[0]?.toUpperCase() || <Users size={12}/>}</div>
                      <div>{u.name}</div>
                    </div>
                  </td>
                  <td className="text-white/70">{u.email}</td>
                  <td><span className="ldc-badge ldc-badge-pending">{u.role}</span></td>
                  <td>
                    <select defaultValue={u.role} onChange={(e) => assignRole(u.user_id, e.target.value)} className="ldc-input rounded-md px-2 py-1 text-xs" data-testid={`urow-select-${u.user_id}`}>
                      {roles.map(r => (<option key={r.key} value={r.key}>{r.name}</option>))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function F({ label, v, on, testid, required }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">{label}</div>
      <input value={v} onChange={(e) => on(e.target.value)} required={required} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid={testid} />
    </label>
  );
}
